import React, { useState, useEffect, useMemo } from 'react';
import { useUserContext } from '../common';
const BASE_URL = `${import.meta.env.VITE_DOMAIN}/v1/api/cagw/certificates`;
export function ListCertificates() {
  const [certificates, setCertificates] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search & Selection State
  const [deaFilter, setDeaFilter] = useState('');
  const [selectedCertIds, setSelectedCertIds] = useState<Set<any>>(new Set());
  // Download & Modal State
  const [isProcessing, setIsProcessing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { user } = useUserContext();
  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setLoading(true);
        const token = user.idToken;
        const response = await fetch(`${BASE_URL}/downloadable`, {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setCertificates(data.certificates || []);
        setCount(data.count || 0);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, []);
  // Filter certificates based on DEA Number
  const filteredCertificates = useMemo(() => {
    return certificates.filter((cert) =>
      cert.deaNumber?.toLowerCase().includes(deaFilter.trim().toLowerCase())
    );
  }, [certificates, deaFilter]);
  // Selection handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = new Set(filteredCertificates.map((cert) => cert.certificateId));
      setSelectedCertIds(allIds);
    } else {
      setSelectedCertIds(new Set());
    }
  };
  const handleToggleSelect = (certId: any) => {
    const updated = new Set(selectedCertIds);
    if (updated.has(certId)) {
      updated.delete(certId);
    } else {
      updated.add(certId);
    }
    setSelectedCertIds(updated);
  };
  // Trigger modal for selected items
  const openBatchPasswordModal = () => {
    if (selectedCertIds.size === 0) return;
    setPassword('');
    setPasswordError('');
    setShowModal(true);
  };
  const closeModal = () => {
    if (isProcessing) return; // Prevent closing during active batch download
    setShowModal(false);
    setPassword('');
    setPasswordError('');
  };
  // Handles download via the new endpoint (handles both single and batch via array)
  const handleBatchDownloadZip = async () => {
    if (!password) {
      setPasswordError('Password is required.');
      return;
    }
    try {
      setIsProcessing(true);
      setPasswordError('');
      const response = await fetch(`${BASE_URL}/p12`, {
        method: 'POST',
        headers: {
          'accept': '/',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.idToken}`
        },
        body: JSON.stringify({
          password: password.split(''),
          certificateRefs: Array.from(selectedCertIds)
        })
      });
      if (!response.ok) {
        throw new Error(`Failed to download certificates: ${response.statusText}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `certificates_batch_${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      // Reset state on success
      setSelectedCertIds(new Set());
      setShowModal(false);
      setPassword('');
    } catch (err: any) {
      setPasswordError(err.message || 'Error creating certificate zip file.');
    } finally {
      setIsProcessing(false);
    }
  };
  if (loading) return <div>Loading certificates...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;
  const isAllSelected =
    filteredCertificates.length > 0 &&
    filteredCertificates.every((cert) => selectedCertIds.has(cert.certificateId));
  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
      <h2>Total Certificates: {count}</h2>
      {/* Controls Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          gap: '12px',
          flexWrap: 'wrap'
        }}
      >
        <input
          type="text"
          placeholder="Filter by DEA Number..."
          value={deaFilter}
          onChange={(e) => setDeaFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            minWidth: '240px'
          }}
        />
        <button
          onClick={openBatchPasswordModal}
          disabled={selectedCertIds.size === 0 || isProcessing}
          style={{
            padding: '8px 16px',
            backgroundColor: selectedCertIds.size === 0 || isProcessing ? '#cccccc' : '#0056b3',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedCertIds.size === 0 || isProcessing ? 'not-allowed' : 'pointer'
          }}
        >
          {isProcessing
            ? 'Creating Zip Archive...'
            : `Download Selected Zip (${selectedCertIds.size})`}
        </button>
      </div>
      {/* Table View */}
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' }}>
        <thead>
          <tr style={{ backgroundColor: '#f2f2f2', textAlign: 'left' }}>
            <th style={{ padding: '12px', borderBottom: '1px solid #ccc', width: '40px' }}>
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
                disabled={filteredCertificates.length === 0}
              />
            </th>
            <th style={{ padding: '12px', borderBottom: '1px solid #ccc' }}>ID</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #ccc' }}>Serial Number</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #ccc' }}>DEA Number</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #ccc' }}>Role</th>
            <th style={{ padding: '12px', borderBottom: '1px solid #ccc' }}>Expiration Date</th>
          </tr>
        </thead>
        <tbody>
          {filteredCertificates.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                No certificates found.
              </td>
            </tr>
          ) : (
            filteredCertificates.map((cert: any) => {
              const isSelected = selectedCertIds.has(cert.certificateId);
              return (
                <tr
                  key={cert.certificateId}
                  style={{
                    borderBottom: '1px solid #eee',
                    backgroundColor: isSelected ? '#f0f7ff' : 'transparent'
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelect(cert.certificateId)}
                    />
                  </td>
                  <td style={{ padding: '12px' }}>{cert.certificateId}</td>
                  <td style={{ padding: '12px' }}>{cert.certSn}</td>
                  <td style={{ padding: '12px' }}>{cert.deaNumber}</td>
                  <td style={{ padding: '12px' }}>{cert.role}</td>
                  <td style={{ padding: '12px' }}>
                    {new Date(cert.certificateExpirationDt).toLocaleDateString()}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {/* Password Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '24px',
              width: '360px',
              maxWidth: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Enter Password for Download</h3>
            <p style={{ fontSize: '14px', color: '#555' }}>
              Generating a single .zip file containing {selectedCertIds.size} certificate(s).
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleBatchDownloadZip();
              }}
              placeholder="Password"
              autoFocus
              style={{
                width: '100%',
                padding: '8px',
                boxSizing: 'border-box',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginBottom: '8px'
              }}
            />
            {passwordError && (
              <p style={{ color: 'red', fontSize: '13px', margin: '4px 0 12px' }}>
                {passwordError}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={closeModal}
                disabled={isProcessing}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#eee',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBatchDownloadZip}
                disabled={isProcessing}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isProcessing ? '#cccccc' : '#0056b3',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer'
                }}
              >
                {isProcessing ? 'Zipping...' : 'Download Zip'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
